import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";

import Chip from "@app/component/Chip";
import EntityChip from "@app/component/EntityChip";
import { RegistryProps } from "@app/component/FieldGrid/utils/ComponentRegistry";
import ListWithOverflowItem from "@app/component/ListWithOverflowItem";
import PeopleOverflowTooltip from "@app/component/PeopleOverflowTooltip";
import { showEntityView } from "@app/store/entityView/actions";
import { doesOrganizationSupportMultiContactActivities } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import getActivityPeople from "@app/util/activity/getActivityPeople";

import styles from "./ActivityPeopleField.module.scss";

interface ActivityPeopleFieldProps extends RegistryProps {
  onShowEntityView: typeof showEntityView;
}

const ActivityPeopleField = ({
  entity,
  isMultiContactEnabled,
  onShowEntityView,
}: ActivityPeopleFieldProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const activity = entity as Activity;
  const sortedPeople = useMemo(() => getActivityPeople(activity), [activity]);

  const handleOpenPerson = useCallback(
    ({ entityId, entityType }: { entityId: number; entityType: EntityType }) => {
      onShowEntityView({ entityId, entityType });
    },
    [onShowEntityView]
  );

  const overflowRender = useCallback(
    (count: number) => (
      <PeopleOverflowTooltip people={sortedPeople.slice(-count)}>
        <span>
          <Chip type="light">
            {intl.formatMessage(
              {
                id: "fieldGrid.field.people.overflowRender",
                defaultMessage: "+ {count} more",
                description: "Overflow render for the activity people pinned field",
              },
              { count }
            )}
          </Chip>
        </span>
      </PeopleOverflowTooltip>
    ),
    [intl, sortedPeople]
  );

  if (!sortedPeople.length) {
    return null;
  }

  // Primary is a multi-contact concept; don't flag a primary person when the add-on is off.
  const primaryId = isMultiContactEnabled ? activity.contact?.id : undefined;

  return (
    <ListWithOverflowItem className={styles.container} overflowRender={overflowRender}>
      {sortedPeople.map((person) => (
        <EntityChip
          entityId={person.id}
          entityType={EntityType.PERSON}
          key={person.id}
          name={person.name ?? ""}
          onClick={handleOpenPerson}
          primary={person.id === primaryId}
          tooltip={intl.formatMessage(
            {
              id: "fieldGrid.field.people.tooltip",
              defaultMessage: "Open {primary, select, true {primary } other{}}person",
              description: "Open person tooltip on the activity people pinned field",
            },
            { primary: person.id === primaryId }
          )}
        />
      ))}
    </ListWithOverflowItem>
  );
};

const mapStateToProps = (state: RootState) => ({
  isMultiContactEnabled: doesOrganizationSupportMultiContactActivities(state),
});

const mapDispatchToProps = {
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityPeopleField);
